/**
 * Annotations — the one player-writable map primitive; structural validity and the point cap
 * only, never geometry.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { MAX_ANNOTATION_POINTS, encounterAnnotations, type EncounterState } from "../types";
import { changed, type EncounterHandler } from "./context";

export const applyAddAnnotation: EncounterHandler<"add_annotation"> = (state, cmd) => {
  // STRUCTURAL validity + the point cap ONLY — the reducer never reads a point's
  // (x, y) value for any geometry/rule decision (the same posture as rooms).
  // Creation is a distinct verb from set_annotation (see the command's
  // doc) so a duplicate id is a hard reject, not a silent replace.
  const annotations = encounterAnnotations(state);
  if (annotations[cmd.annotation.id]) {
    return err(new CommandError(`add_annotation: an annotation with id ${cmd.annotation.id} already exists (use set_annotation to replace it)`));
  }
  if (cmd.annotation.points.length > MAX_ANNOTATION_POINTS) {
    return err(
      new CommandError(`add_annotation: ${cmd.annotation.points.length} points exceeds the ${MAX_ANNOTATION_POINTS}-point cap`),
    );
  }
  const next: EncounterState = {
    ...state,
    annotations: { ...annotations, [cmd.annotation.id]: cmd.annotation },
  };
  return ok({
    state: next,
    inverse: { type: "remove_annotation", actorId: cmd.actorId, annotationId: cmd.annotation.id },
    events: changed(null),
  });
};

export const applyRemoveAnnotation: EncounterHandler<"remove_annotation"> = (state, cmd) => {
  // Reject an unknown annotation id (a sanity check, like remove_room). The
  // inverse is an add_annotation re-adding the captured annotation (round-trip
  // exact). Purely visual — no mechanical fallout.
  const annotations = encounterAnnotations(state);
  const removed = annotations[cmd.annotationId];
  if (!removed) {
    return err(new CommandError(`remove_annotation: unknown annotation ${cmd.annotationId}`));
  }
  const { [cmd.annotationId]: _gone, ...rest } = annotations;
  const next: EncounterState = { ...state, annotations: rest };
  return ok({
    state: next,
    inverse: { type: "add_annotation", actorId: cmd.actorId, annotation: removed },
    events: changed(null),
  });
};

export const applySetAnnotation: EncounterHandler<"set_annotation"> = (state, cmd) => {
  // Replace an EXISTING annotation wholesale (recolor, or a points rewrite) —
  // rejected if the id is unknown (add_annotation is the creation path; see the
  // command's doc for why the two stay distinct verbs). The inverse is
  // SYMMETRIC: set_annotation restoring the prior annotation (set↔set-prior,
  // exactly mirroring set_room's create/replace symmetry, but here always a
  // replace since creation lives in add_annotation).
  const annotations = encounterAnnotations(state);
  const prev = annotations[cmd.annotation.id];
  if (!prev) {
    return err(new CommandError(`set_annotation: unknown annotation ${cmd.annotation.id} (use add_annotation to create it)`));
  }
  if (cmd.annotation.points.length > MAX_ANNOTATION_POINTS) {
    return err(
      new CommandError(`set_annotation: ${cmd.annotation.points.length} points exceeds the ${MAX_ANNOTATION_POINTS}-point cap`),
    );
  }
  const next: EncounterState = {
    ...state,
    annotations: { ...annotations, [cmd.annotation.id]: cmd.annotation },
  };
  return ok({
    state: next,
    inverse: { type: "set_annotation", actorId: cmd.actorId, annotation: prev },
    events: changed(null),
  });
};
